// Review-surface consolidation §7 — align every portal tab with the
// internal admin app's page-header pattern: a gold uppercase eyebrow naming
// the portal type (derived from accountType), a large display heading naming
// the tab, and a one-sentence muted subtitle. Reuses the shared
// <PageHeader> primitive (which uppercases the eyebrow + title via CSS) so
// the portal and staff surfaces stay visually consistent.

import { PageHeader } from "@/design-system/components/ui";
import type { MaterialAudience } from "../api/types";
import type { PortalTabId } from "./PortalTabMatrix";

const TAB_META: Record<PortalTabId, { heading: string; subtitle: string }> = {
  // Journey-Stage Adaptive Dashboards — the per-type landing surface. Each
  // dashboard passes a stage-appropriate subtitle override; this is the fallback.
  home: {
    heading: "Home",
    subtitle: "Where you are in your journey and what to do next.",
  },
  requests: {
    heading: "Requests",
    subtitle: "Documents, information, and training for you to complete.",
  },
  documents: {
    heading: "Documents",
    subtitle: "Everything you've uploaded or signed, in one place.",
  },
  files: {
    heading: "Files & library",
    subtitle: "Reference materials and resources we've published for you.",
  },
  // TPS Servicing Portal PR-C — the servicer's assigned-loans roster.
  loans: {
    heading: "Loans",
    subtitle: "Loans assigned to you for servicing. Review each one, then accept or decline.",
  },
  // TPS Servicing Portal PR-D — the firm's per-state debt-collector disclosure.
  "debt-collector-language": {
    heading: "Debt-collector language",
    subtitle:
      "Your borrower-facing debt-collector disclosure for each state. Phoenix primes a default; review with counsel, then approve. An approved disclosure is required before you can accept a loan for borrowers in that state.",
  },
  bills: {
    heading: "Bills",
    subtitle:
      "Monthly statements Phoenix prepares on your behalf. Review each bill, then approve it to post to the borrower or return it with a note for correction.",
  },
  // TPS Servicing Portal PR-G — the firm's money tabs.
  distributions: {
    heading: "Distributions",
    subtitle:
      "Approve each cleared payment's distribution; funds pay out of your clearing account.",
  },
  commissions: {
    heading: "Commissions",
    subtitle: "Your earned servicing-fee income.",
  },
  // TPS Servicing Portal PR-H — the firm's default/collection cases.
  collections: {
    heading: "Collections",
    subtitle:
      "Loans in default assigned to you for workout and collection. Add notes and record recoveries — you earn your agreed fee on amounts collected.",
  },
  // TPS Servicing Portal PR-I — the firm's 1099 management surface (payer view)
  // and every recipient's read-only "my forms" surface. The default subtitle
  // reads for recipients (the majority); the TPS page passes a payer override.
  "tax-forms": {
    heading: "Tax forms",
    subtitle: "1099 information returns issued to you.",
  },
  deals: {
    heading: "Deals & status",
    subtitle: "The current status and stage of your deals.",
  },
  // Partner Brand Ownership — the completions the caller's own prequalification
  // site produced (broker + brokerage). Scoped server-side to whichever site
  // resolves for the authenticated account.
  prequalification: {
    heading: "Prequalification",
    subtitle: "Everyone who has completed a prequalification on your site.",
  },
  payments: {
    heading: "Payments & ledger",
    subtitle: "Your running balance, scheduled payments, and statements.",
  },
  // Broker Commission Modeler — the broker's private what-if scratchpad. The
  // subtitle leads with the privacy fact because that is the question a broker
  // asks first about a tool that names their client and their fee.
  "commission-modeler": {
    heading: "Commission Modeler",
    subtitle:
      "Sketch a deal and see what it earns you — at closing and every month. Private to you: nothing here is submitted or attached to a deal.",
  },
  // Payment Setup — the guided bank/ACH setup surface (buyer bank-connect;
  // seller/broker Stripe payout onboarding).
  "payment-setup": {
    heading: "Payment setup",
    subtitle: "Connect your bank account so your money moves smoothly and securely.",
  },
  notifications: {
    heading: "Notifications",
    subtitle: "Everything we've sent you and everything that needs attention.",
  },
  contributions: {
    heading: "Income & SPV contributions",
    subtitle: "Capital contributions, distributions, and per-SPV income.",
  },
  invoices: {
    heading: "Invoices",
    subtitle: "Your invoices, with a per-client summary once you have more than one.",
  },
  settings: {
    heading: "Account & settings",
    subtitle: "Manage your profile and password.",
  },
  // Vendor Program v2 PR 2 — the monthly cycle.
  goals: {
    heading: "My goals",
    subtitle: "Your improvement goals and the monthly progress behind them.",
  },
  reports: {
    heading: "Monthly reports",
    subtitle: "Your monthly client reports — drafts, submissions, and outcomes.",
  },
  performance: {
    heading: "Performance",
    subtitle: "How your work is rated across your assigned clients.",
  },
  // Annual Vendor Audit — the yearly market check on the vendor's pricing. The
  // subtitle leads with the fact the vendor cares about most: this is not
  // finished, and their answer is part of it. 🔴 `heading` must match the
  // `label` on ANNUAL_AUDIT in PortalTabMatrix — two independent literals, no
  // shared constant, nothing testing that they agree.
  "annual-audit": {
    heading: "Annual market check",
    subtitle:
      "Once a year we check that what we pay you is still in line with the market. Read what we found and tell us what we've missed — we finish our assessment after you've had your say.",
  },
  // Vendor Connection v1 — the explicit two-sided connection.
  vendors: {
    heading: "My Vendors",
    subtitle: "The vendors connected to your loan — their goals, monthly report cards, and your ratings.",
  },
  customers: {
    heading: "Customers",
    subtitle: "Your connected customers — contact details, assigned goals, and your signed agreements.",
  },
  // Brokerage / Sub-Broker portal — the firm's roster of affiliated sub-brokers
  // and a firm-wide activity feed. (The dedicated BrokerageDashboardPage renders
  // its own header; these back the exhaustive record.)
  roster: {
    heading: "Sub-brokers",
    subtitle: "Your affiliated sub-brokers and their production.",
  },
  activity: {
    heading: "Activity",
    subtitle: "Recent activity across your firm.",
  },
  // Brokerage / Sub-Broker PR-7 — the broker-side affiliation consent surface.
  affiliations: {
    heading: "Affiliation requests",
    subtitle: "Brokerages inviting you to join. Accept to join a firm (leaving your current one) or decline.",
  },
  // Lead Generation console — the external generator's four sections. Home reuses
  // the shared entry above; these three carry the console's own copy. (The
  // console page renders its own header, so these back the exhaustive record.)
  "lg-preleads": {
    heading: "Production line",
    subtitle: "Paste a listing, extract the details, and advance each prelead one click at a time.",
  },
  "lg-earnings": {
    heading: "Earnings",
    subtitle: "Your weekly KPI credits, caps, and where each credit stands.",
  },
  "lg-feedback": {
    heading: "Feedback",
    subtitle: "Coaching notes from your manager.",
  },
};

export function PortalTabHeader({
  accountType,
  tab,
  subtitle,
}: {
  accountType: MaterialAudience;
  tab: PortalTabId;
  /** Override the default subtitle for a tab when the same tab id renders
   *  different content per account type (e.g. the broker Deals tab hosts
   *  the submissions dashboard, not the recipient read-only mirror). */
  subtitle?: string;
}) {
  const meta = TAB_META[tab];
  return (
    <PageHeader
      eyebrow={`${accountType} portal`}
      title={meta.heading}
      description={subtitle ?? meta.subtitle}
    />
  );
}
